import { format } from 'date-fns'
import { FieldArray, type FormikHelpers, useFormikContext } from 'formik'
import * as React from 'react'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import DateInput from 'src/views/components/forms/formik/DateInput'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import PopoverComponent from 'src/views/components/PopoverComponent'
import Select from 'src/views/components/Select'
import { CRITERIA_TYPE } from './constants'
import { criteriaBandErrors } from './validation'

interface CriteriaRowValues {
  readonly criteriaId: number
  readonly type: number
  readonly min: string
  readonly max: string
  readonly deadline: Date | null
}

interface CriteriasFormValues {
  readonly intermediateScoreLimit: string
  readonly criterias: readonly CriteriaRowValues[]
}

interface CriteriaOption {
  readonly value: number
  readonly label: string
}

interface Props {
  readonly headers: AuthenticatedHeaders
  readonly courseId: string
  readonly groupId: number
  readonly semId: number
  readonly data: Api.AdministratorCourseGroupCriterias | null
  readonly isPending: boolean
  /** Extra groups picked in the groups table; the edited group is always included by the API. */
  readonly pickedGroupIds: readonly number[]
  readonly onSaved: () => Promise<void>
  /** Rendered left of Save — the paper's activate/deactivate toggle. */
  readonly footerActions?: React.ReactNode
}

export default function PaperCriteriasForm({
  headers,
  courseId,
  groupId,
  semId,
  data,
  isPending,
  pickedGroupIds,
  onSaved,
  footerActions,
}: Props): React.ReactElement {
  const t = useTranslatable()
  const [checkedCriterias, setCheckedCriterias] = React.useState<readonly number[]>([])

  const paper = data?.paper ?? null
  const availableCriterias = React.useMemo(() => data?.availableCriterias ?? [], [data?.availableCriterias])

  const initialValues = React.useMemo<CriteriasFormValues>(() => {
    // A group without a paper starts from the two criteria every paper must carry.
    const seed = availableCriterias.filter(
      (criteria) => criteria.type === CRITERIA_TYPE.FINAL_EVALUATION || criteria.type === CRITERIA_TYPE.REMEDIATION_EXAM
    )

    const rows: CriteriaRowValues[] =
      paper != null
        ? paper.criterias.map((criteria) => ({
            criteriaId: criteria.id,
            type: criteria.type,
            min: String(criteria.min),
            max: String(criteria.max),
            deadline: criteria.deadline != null && criteria.deadline !== '' ? new Date(criteria.deadline) : null,
          }))
        : seed.map((criteria) => ({
            criteriaId: criteria.id,
            type: criteria.type,
            min: '1',
            max: String(criteria.maxPossible),
            deadline: null,
          }))

    return { intermediateScoreLimit: String(paper?.intermediateScoreLimit ?? 0), criterias: rows }
  }, [availableCriterias, paper])

  React.useEffect(() => {
    setCheckedCriterias((paper?.criterias ?? []).map((criteria) => criteria.id))
  }, [paper])

  const handleSubmit = React.useCallback(
    async (values: CriteriasFormValues, helpers: FormikHelpers<CriteriasFormValues>) => {
      // The API rejects each of these with an untranslated or misleading 422, so catch them here.
      const bandErrors: Record<string, string[]> = {}
      values.criterias.forEach((row, index) => {
        const included = row.type !== CRITERIA_TYPE.INTERMEDIATE || checkedCriterias.includes(row.criteriaId)
        if (!included) return
        const { min, max } = criteriaBandErrors(row.type, row.min, row.max, t)
        const maxPossible = availableCriterias.find((criteria) => criteria.id === row.criteriaId)?.maxPossible
        const maxErrors = [...max]
        if (maxPossible != null && Number(row.max) > maxPossible) {
          maxErrors.push(`${t('course:criteria_max_short')} ${maxPossible}`)
        }
        if (min.length > 0) bandErrors[`criterias.${index}.min`] = [...min]
        if (maxErrors.length > 0) bandErrors[`criterias.${index}.max`] = maxErrors
      })
      if (Object.keys(bandErrors).length > 0) {
        throw { type: 'ErrorsObject', errors: bandErrors }
      }

      const criterias = values.criterias
        .filter((row) => row.type !== CRITERIA_TYPE.INTERMEDIATE || checkedCriterias.includes(row.criteriaId))
        .map((row) => ({
          criteriaId: row.criteriaId,
          min: Number(row.min),
          max: Number(row.max),
          ...(row.deadline != null && { deadline: format(row.deadline, 'yyyy-MM-dd') }),
        }))

      try {
        await Api.putAdministratorCourseGroupCriterias({
          headers,
          args: { id: courseId, groupId },
          body: {
            semId,
            intermediateScoreLimit: Number(values.intermediateScoreLimit),
            criterias,
            ...(pickedGroupIds.length > 0 && { groupIds: [...pickedGroupIds] }),
          },
        })
        // Mark the form clean first, or its unsaved-changes guard blocks the return to the marks.
        helpers.resetForm({ values })
        await onSaved()
        toast.success(
          paper == null ? t('uncategorized:paper_successfully_added') : t('uncategorized:paper_successfully_updated')
        )
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
        throw error
      }
    },
    [availableCriterias, checkedCriterias, courseId, groupId, headers, onSaved, paper, pickedGroupIds, semId, t]
  )

  if (isPending) return <Loader className="m-auto flex" />
  if (data == null) return <NoContent header={t('common:records_not_found')} image={Warning} marginTop="5" />

  return (
    <div className="w-full">
      <Form initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize isConfirmable>
        <FormError />
        <CriteriasFormContent
          availableCriterias={availableCriterias}
          checkedCriterias={checkedCriterias}
          setCheckedCriterias={setCheckedCriterias}
        />
        <div className="mt-2 flex w-full justify-end gap-2">
          {footerActions}
          <Button variant="blue" type="submit">
            {t('common:save')}
          </Button>
        </div>
      </Form>
    </div>
  )
}

function CriteriasFormContent({
  availableCriterias,
  checkedCriterias,
  setCheckedCriterias,
}: {
  readonly availableCriterias: readonly Api.AdministratorPaperAvailableCriteria[]
  readonly checkedCriterias: readonly number[]
  readonly setCheckedCriterias: React.Dispatch<React.SetStateAction<readonly number[]>>
}): React.ReactElement {
  const t = useTranslatable()
  const formik = useFormikContext<CriteriasFormValues>()
  const { criterias } = formik.values

  const criteriaById = React.useMemo(() => {
    const map = new Map<number, Api.AdministratorPaperAvailableCriteria>()
    for (const criteria of availableCriterias) map.set(criteria.id, criteria)
    return map
  }, [availableCriterias])

  const finalEvaluation = React.useMemo(
    () => criterias.find((row) => row.type === CRITERIA_TYPE.FINAL_EVALUATION),
    [criterias]
  )
  const remediationIndex = React.useMemo(
    () => criterias.findIndex((row) => row.type === CRITERIA_TYPE.REMEDIATION_EXAM),
    [criterias]
  )

  // Legacy mirrors the final evaluation's band onto the remediation exam, which is never edited directly.
  React.useEffect(() => {
    if (remediationIndex === -1 || finalEvaluation == null) return
    void formik.setFieldValue(`criterias.${remediationIndex}.min`, finalEvaluation.min)
    void formik.setFieldValue(`criterias.${remediationIndex}.max`, finalEvaluation.max)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalEvaluation?.min, finalEvaluation?.max, remediationIndex])

  const criteriaOptions = React.useMemo<readonly CriteriaOption[]>(
    () =>
      availableCriterias
        .filter(
          (criteria) =>
            criteria.type === CRITERIA_TYPE.INTERMEDIATE && !criterias.some((row) => row.criteriaId === criteria.id)
        )
        .map((criteria) => ({ value: criteria.id, label: criteria.name.trim() })),
    [availableCriterias, criterias]
  )

  const addCriteria = React.useCallback(
    (option: CriteriaOption | null) => {
      if (option == null) return
      setCheckedCriterias((prev) => (prev.includes(option.value) ? prev : [...prev, option.value]))
      // Keep the exam criteria last, the way both legacy and the API order a paper.
      const endIndex = criterias.findIndex((row) => row.type !== CRITERIA_TYPE.INTERMEDIATE)
      const insertIndex = endIndex === -1 ? criterias.length : endIndex
      const next = [...criterias]
      next.splice(insertIndex, 0, {
        criteriaId: option.value,
        type: CRITERIA_TYPE.INTERMEDIATE,
        min: '0',
        max: '',
        deadline: null,
      })
      void formik.setFieldValue('criterias', next)
    },
    [criterias, formik, setCheckedCriterias]
  )

  const toggleCriteria = React.useCallback(
    (criteriaId: number) => {
      setCheckedCriterias((prev) =>
        prev.includes(criteriaId) ? prev.filter((id) => id !== criteriaId) : [...prev, criteriaId]
      )
    },
    [setCheckedCriterias]
  )

  return (
    <FieldArray name="criterias">
      {() => (
        <>
          <label htmlFor="criterions" className="text-primaryTextColor">
            <span className="font-bold">
              {t('course:select_criteria_from_the_list_and_write_point_relevant_to_criteria')}
            </span>
            <Select<CriteriaOption>
              className="my-3"
              inputId="criterions"
              placeholder={t('course:select_criteria')}
              options={criteriaOptions}
              value={null}
              onChange={addCriteria}
              isDisabled={formik.isSubmitting}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              menuPosition="fixed"
            />
          </label>
          <Table.Table className="mt-6">
            <Table.Thead>
              <Table.Tr>
                <Table.Th scope="col" />
              </Table.Tr>
              <Table.Tr>
                <Table.Th scope="col" />
                <Table.Th scope="col" className="pl-0 text-start">
                  {t('course:assessment_components')}
                </Table.Th>
                <Table.Th scope="col" className="text-start">
                  {t('common:min')}
                </Table.Th>
                <Table.Th scope="col" className="whitespace-nowrap text-center">
                  {t('common:max')}
                </Table.Th>
                <Table.Th scope="col" className="whitespace-nowrap text-center">
                  {t('course:criteria_deadline')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td />
                <Table.Td>{t('course:intermediate_score_limit')} :</Table.Td>
                <Table.Td>
                  <div className="w-[calc(100%-22px)]">
                    <TextInput required name="intermediateScoreLimit" type="number" numberArrowKeysFloatStep />
                  </div>
                </Table.Td>
                <Table.Td />
                <Table.Td />
              </Table.Tr>
              {criterias.map((row, index) => (
                <CriteriaRow
                  key={row.criteriaId}
                  row={row}
                  index={index}
                  name={criteriaById.get(row.criteriaId)?.name.trim() ?? String(row.criteriaId)}
                  maxPossible={criteriaById.get(row.criteriaId)?.maxPossible ?? null}
                  isChecked={checkedCriterias.includes(row.criteriaId)}
                  onToggle={toggleCriteria}
                />
              ))}
            </Table.Tbody>
          </Table.Table>
        </>
      )}
    </FieldArray>
  )
}

function CriteriaRow({
  row,
  index,
  name,
  maxPossible,
  isChecked,
  onToggle,
}: {
  readonly row: CriteriaRowValues
  readonly index: number
  readonly name: string
  readonly maxPossible: number | null
  readonly isChecked: boolean
  readonly onToggle: (criteriaId: number) => void
}): React.ReactElement {
  const t = useTranslatable()
  const isIntermediate = row.type === CRITERIA_TYPE.INTERMEDIATE
  const isFinal = row.type === CRITERIA_TYPE.FINAL_EVALUATION
  const isRemediation = row.type === CRITERIA_TYPE.REMEDIATION_EXAM
  // The final evaluation is always part of the paper; the remediation exam mirrors it.
  const disabled = isRemediation || (isIntermediate && !isChecked)

  const handleToggle = React.useCallback(() => onToggle(row.criteriaId), [onToggle, row.criteriaId])

  return (
    <Table.Tr>
      <Table.Td className="rounded-l-lg pt-3 !align-baseline">
        {isIntermediate && (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={handleToggle}
            className="cursor-pointer border border-borderColor dark:bg-primaryWhite"
          />
        )}
      </Table.Td>
      <Table.Td className="pt-3 !align-baseline">
        <div>
          {name}
          {maxPossible != null && (
            <span className="ml-1 text-captionColor" style={{ fontSize: '12px' }}>
              ({t('course:criteria_max_short')} {maxPossible})
            </span>
          )}
        </div>
      </Table.Td>
      <Table.Td className="!align-baseline">
        <div className="item-center flex">
          <TextInput
            name={`criterias.${index}.min`}
            type="number"
            numberArrowKeysFloatStep
            disabled={disabled}
            required
          />
          <div className="item-center mb-3 flex">
            <PopoverComponent>
              <span className="flex flex-col">
                {isFinal ? t('course:final_evaluation_min_exceeds_60_percent') : t('course:minimum_value')}
              </span>
            </PopoverComponent>
          </div>
        </div>
      </Table.Td>
      <Table.Td className="!align-baseline">
        <div className="item-center flex">
          <TextInput
            name={`criterias.${index}.max`}
            type="number"
            numberArrowKeysFloatStep
            disabled={disabled}
            required
            {...(maxPossible != null && { max: maxPossible })}
          />
          <div className="item-center mb-3 flex">
            <PopoverComponent>
              <span className="flex flex-col">{t('course:maxsimum_value')}</span>
            </PopoverComponent>
          </div>
        </div>
      </Table.Td>
      <Table.Td className="rounded-r-lg !align-baseline">
        <DateInput name={`criterias.${index}.deadline`} disabled={disabled} />
      </Table.Td>
    </Table.Tr>
  )
}
